import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Select,
  MenuItem,
  InputLabel,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Paper,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  API_BASE_URL,
  RUBROSLISTAR,
  PRODUCTOSLISTS,
  PRODUCTOSORDENAR,
  RUTAPRODUCTOSIMAGENES,
} from '../services/apiConfig';
import { MenuInicial } from './MenuInicial';
import { registrarAuditoria, isAuthenticated } from '../services/api.js';
import Cargando from './utils/Cargando.jsx';

const SortableProducto = ({ producto }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: producto.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: '#fff',
  };

  return (
    <ListItem ref={setNodeRef} style={style} divider>
      <span {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center', marginRight: '12px' }}>
        <DragIndicatorIcon />
      </span>
      <ListItemAvatar>
        <Avatar
          variant="rounded"
          src={producto.fotos ? RUTAPRODUCTOSIMAGENES + '/' + producto.fotos : undefined}
        />
      </ListItemAvatar>
      <ListItemText primary={producto.nombre} secondary={`$${producto.precio}`} />
    </ListItem>
  );
};

const ProductoOrdenar = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(sessionStorage.getItem('userData'));
  const usuarioid = userData?.usuarioid;

  const [rubros, setRubros] = useState([]);
  const [selectedRubro, setSelectedRubro] = useState('');
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState('');

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    const autenticado = isAuthenticated();
    if (autenticado === false) {
      navigate('/login');
    }

    const fetchRubros = async () => {
      try {
        const response = await axios.get(API_BASE_URL + RUBROSLISTAR);
        setRubros(response.data);
      } catch (error) {
        console.error('Error al obtener los rubros:', error);
      }
    };

    fetchRubros();
  }, [navigate]);

  const handleRubroChange = async (event) => {
    const rubroid = event.target.value;
    setSelectedRubro(rubroid);
    setMensaje('');
    setLoading(true);
    try {
      const response = await axios.get(API_BASE_URL + PRODUCTOSLISTS);
      const productosDelRubro = (Array.isArray(response.data) ? response.data : [])
        .filter((p) => String(p.rubroid) === String(rubroid))
        .sort((a, b) => (Number(a.orden) || 0) - (Number(b.orden) || 0));
      setProductos(productosDelRubro);
    } catch (error) {
      console.error('Error al obtener los productos:', error);
    }
    setLoading(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setProductos((items) => {
      const oldIndex = items.findIndex((p) => p.id === active.id);
      const newIndex = items.findIndex((p) => p.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const handleGuardar = async () => {
    if (!selectedRubro || productos.length === 0) return;
    setSaving(true);
    setMensaje('');
    try {
      await axios.post(API_BASE_URL + PRODUCTOSORDENAR, {
        rubroid: selectedRubro,
        orden: productos.map((p) => p.id),
      });

      const rubroNombre = rubros.find((r) => String(r.id) === String(selectedRubro))?.nombre || selectedRubro;
      await registrarAuditoria(
        'Reordenar',
        'Producto',
        rubroNombre,
        'orden',
        '',
        '',
        'ProductoOrdenar',
        usuarioid
      );

      setMensaje('Orden guardado correctamente.');
    } catch (error) {
      console.error('Error al guardar el orden:', error);
      setMensaje('Hubo un error al guardar el orden.');
    }
    setSaving(false);
  };

  return (
    <Container>
      <MenuInicial />
      <div className="miDiv"><h2>Ordenar Productos</h2></div>

      <InputLabel id="rubro-orden-label">Rubro</InputLabel>
      <Select
        labelId="rubro-orden-label"
        value={selectedRubro}
        onChange={handleRubroChange}
        fullWidth
        style={{ marginBottom: '16px' }}
      >
        {rubros.map((rubro) => (
          <MenuItem key={rubro.id} value={rubro.id}>
            {rubro.nombre}
          </MenuItem>
        ))}
      </Select>

      {loading ? (
        <Cargando></Cargando>
      ) : (
        selectedRubro && (
          <>
            {productos.length === 0 ? (
              <Typography>Este rubro no tiene productos activos.</Typography>
            ) : (
              <Paper>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={productos.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                    <List>
                      {productos.map((producto) => (
                        <SortableProducto key={producto.id} producto={producto} />
                      ))}
                    </List>
                  </SortableContext>
                </DndContext>
              </Paper>
            )}

            <div style={{ marginTop: '16px' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGuardar}
                disabled={saving || productos.length === 0}
              >
                {saving ? 'Guardando...' : 'Guardar orden'}
              </Button>
              {mensaje && (
                <Typography style={{ marginTop: '8px' }}>{mensaje}</Typography>
              )}
            </div>
          </>
        )
      )}
    </Container>
  );
};

export default ProductoOrdenar;
